import React, { useRef, useEffect } from "react";
import JsBarcode from "jsbarcode";

/**
 * Hitung EAN-13 check digit dari 12 digit pertama.
 * Return string 13 digit dengan check digit yang benar.
 */
function fixEAN13Checksum(digits12) {
  const d = String(digits12).slice(0, 12).padStart(12, "0");
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(d[i], 10) * (i % 2 === 0 ? 1 : 3);
  }
  const check = (10 - (sum % 10)) % 10;
  return d + check;
}

/**
 * Validasi EAN-13 checksum.
 */
function isValidEAN13(value) {
  if (!/^\d{13}$/.test(value)) return false;
  const corrected = fixEAN13Checksum(value.slice(0, 12));
  return corrected === value;
}

/**
 * BarcodeSVG — render barcode via JsBarcode (MIT license).
 *
 * Auto-detect format:
 *   - 13 digit  → EAN-13  (standard retail, compact, checksum auto-fix)
 *   - 12 digit  → EAN-13  (auto-append check digit)
 *   - 8 digit   → EAN-8
 *   - lainnya   → CODE128
 *
 * Props:
 *   value        — string kode barcode
 *   height       — tinggi batang dalam px
 *   width        — lebar satu modul (px). Default 1.8
 *   maxWidth     — jika > 0, width dihitung agar muat dalam maxWidth px
 *   showText     — tampilkan digit di bawah barcode
 *   fontSize     — ukuran font angka (px)
 *   className    — class tambahan pada wrapper div
 */
export function BarcodeSVG({
  value = "8991234567001",
  height = 40,
  width: barWidth = 1.8,
  maxWidth = 0,
  showText = true,
  fontSize = 10,
  className = "",
}) {
  const svgRef = useRef(null);
  const safeValue = String(value || "8991234567001").trim();
  const isAllDigits = /^\d+$/.test(safeValue);

  // Tentukan format dan value yang akan di-render
  let format = "CODE128";
  let renderValue = safeValue;

  if (isAllDigits && safeValue.length === 13) {
    format = "EAN13";
    // Auto-fix checksum untuk render — agar selalu pakai EAN-13 yang compact
    // (jika checksum salah, koreksi dari 12 digit pertama)
    if (!isValidEAN13(safeValue)) {
      renderValue = fixEAN13Checksum(safeValue.slice(0, 12));
    }
  } else if (isAllDigits && safeValue.length === 12) {
    format = "EAN13";
    renderValue = fixEAN13Checksum(safeValue);
  } else if (isAllDigits && safeValue.length === 8) {
    format = "EAN8";
  }

  // Estimasi modul berdasarkan format untuk maxWidth calculation
  // EAN-13: 95 modul + 2 quiet zone (9 unit masing-masing) = 113 unit total
  // CODE128: bervariasi ~150-180 modul untuk 13 karakter
  const estimatedUnits =
    format === "EAN13" ? 113 : format === "EAN8" ? 67 : 165;

  let w = Number(barWidth) || 1.6;
  if (maxWidth > 0) {
    const wFromMax = (maxWidth - 2) / estimatedUnits;
    w = Math.min(w, wFromMax);
  }
  // Cap max w to 0.88 for tight, crisp bars on thermal printers
  w = Math.min(0.88, Math.max(0.65, w));

  const finalFontSize = showText ? Math.max(6, Math.min(9, fontSize)) : 0;

  useEffect(() => {
    if (!svgRef.current) return;
    try {
      JsBarcode(svgRef.current, renderValue, {
        format,
        width: w,
        height,
        displayValue: showText,
        fontSize: finalFontSize,
        margin: 0,
        marginTop: 0,
        marginBottom: 1,
        marginLeft: 0,
        marginRight: 0,
        background: "#ffffff",
        lineColor: "#000000",
        fontOptions: "",
        font: "sans-serif",
        textMargin: 1,
        flat: false,
      });
    } catch (e) {
      // Fallback CODE128 dengan lebar sangat kompak
      try {
        JsBarcode(svgRef.current, safeValue, {
          format: "CODE128",
          width: Math.min(w * 0.65, 1.0),
          height,
          displayValue: showText,
          fontSize: showText ? fontSize : 0,
          margin: 0,
          marginBottom: 1,
          background: "#ffffff",
          lineColor: "#000000",
          textMargin: showText ? 1 : 0,
        });
      } catch (_) {
        // silent — SVG tetap kosong
      }
    }
  }, [renderValue, safeValue, w, height, showText, fontSize, format]);

  return (
    <div className={`inline-flex items-start select-none ${className}`}>
      <svg ref={svgRef} style={{ overflow: "visible", display: "block" }} />
    </div>
  );
}

export default BarcodeSVG;
